import intersection from 'lodash/intersection';
import zip from 'lodash/zip';
import { getDefinition } from './components/FullscreenPreview/model';
import { CORE_DIRECTORY_FLAGS, HasFlags, INode, NodeFlag, NodeType, Tag } from './model';
import { asPlotRecognitionStatus, PlotRecognitionStatus } from './modelPlotRecognition';
import { SortByInterface } from './utils/useSort';
import { z } from 'zod';

export type NodeLabel = Pick<CHNode, 'id' | 'name'>;

export const NodeLabelSchema = z.object({
  id: z.number(),
  name: z.string(),
});

export interface CHNode extends HasFlags {
  id: number;
  name: string;
  created_at: string;
  updated_at: string;
  type: string;
  tags: Tag[];
  perms: string[];
  download_url: string | null;
  can_download_direct: boolean;
  direct_source: string | null;
  preview_url: string | null;
  size: number | null;
  is_linked?: boolean;
  // Разрешено ли скачивание ноды. Бэкенд присылает флаг (и в списке, и в деталях);
  // для файлов под модерацией/отклонённых он приходит false. Отсутствие поля трактуем
  // как true — по аналогии с Asset Lab (MYUA-16456).
  downloading_allowed?: boolean;
  // Есть ли у креатива успешная AI-разметка сюжета (MYUA-16423). Приходит только
  // из /fs/list_folder; в шаринге и в текстовом поиске поля нет — отсутствие трактуем как false.
  has_plot_recognition?: boolean;
  // Разметка сюжета в ответе семантического поиска (MYUA-16771): `/fs/list_folder/search`
  // при `search_type=semantic` кладёт её прямо в ноду, а `has_plot_recognition` не отдаёт.
  // Содержимое не типизируем — фронт его пока не читает, важен лишь сам факт наличия;
  // при выводе разметки в выдаче поиска заменить на подмножество PlotRecognitionServer.
  plot_recognition?: object | null;
  // Статус AI-разметки сюжета (MYUA-16736), источник тот же, что у has_plot_recognition:
  // /fs/list_folder. Строка, а не union: неизвестное бэкендовое значение нормализуем
  // в CHNodePlotRecognitionStatus.
  plot_recognition_status?: string | null;
}

export const NodePathParams = z.object({
  path: z.string(),
  abs_path: z.string(),
});

// eslint-disable-next-line @typescript-eslint/no-redeclare
export type NodePathParams = z.infer<typeof NodePathParams>;

export function getParent(npp: NodePathParams) {
  const parentDirNodeID = npp.path.split('.').at(-2);
  if (parentDirNodeID === undefined) throw new Error();
  const parentDirNodeName = npp.abs_path.split('/').at(-2);
  if (parentDirNodeName === undefined) throw new Error();
  const parentDirPath = npp.abs_path.substring(0, npp.abs_path.lastIndexOf('/'));
  return { id: parseInt(parentDirNodeID), path: parentDirPath, name: parentDirNodeName };
}
export const CHNodeToString = (node: INode | CHNode) => `[${node.id}]${node.name}`;

export const CHNodeIsFolder = (self: INode | CHNode) => {
  return self.type === NodeType.folder;
};

export const CHNodeCheckName = (name: string) => {
  if (name.trim().length === 0) {
    return false;
  }
  return true;
};

export type SomeNodeWithAdditionalFields<AdditionalFields> = (CHNode | INode) & AdditionalFields;
export function INodeToCHNode<AdditionalFields extends {}>(
  node: SomeNodeWithAdditionalFields<AdditionalFields>,
): CHNode & AdditionalFields {
  return {
    ...node,
    is_linked: node.is_linked ?? false,
    perms: node.perms || [],
  };
}
// Разрешено ли скачивание ноды. Решение принимает бэкенд (флаг downloading_allowed):
// для файлов под модерацией/отклонённых он приходит false. Отсутствие флага = разрешено.
// Скачивание запрещено ⇒ также запрещены шаринг и прямая ссылка Google Drive (MYUA-16456).
export const isNodeDownloadAllowed = (node: Pick<CHNode, 'downloading_allowed'>): boolean =>
  node.downloading_allowed !== false;

export const CHNodeIsCreativeFolder = (self: CHNode | INode) => {
  return CHNodeIsFolder(self) && self.flags.includes(NodeFlag.folder_creative);
};
// Индикатор AI-разметки в списке: только на уровне папки-креатива (MYUA-16423).
// Два источника признака: `has_plot_recognition` в листинге папки и вложенная разметка
// `plot_recognition` в выдаче семантического поиска (MYUA-16771) — сериализаторы у этих
// ручек разные, и второй флага не отдаёт. Без учёта обоих иконка пропадала бы на /search.
export const CHNodeHasPlotRecognition = (self: CHNode | INode) =>
  CHNodeIsCreativeFolder(self) &&
  (self.has_plot_recognition === true ||
    (self.plot_recognition !== null && self.plot_recognition !== undefined));
// Статус AI-разметки для столбца `Recognition` (MYUA-16736): только у папки-креатива,
// неизвестное значение и отсутствие поля -> null («нет разметки»).
export const CHNodePlotRecognitionStatus = (self: CHNode | INode): PlotRecognitionStatus | null =>
  CHNodeIsCreativeFolder(self) ? asPlotRecognitionStatus(self.plot_recognition_status) : null;
export function CHNodeHasPathParams(self: CHNode): self is CHNode & NodePathParams;
export function CHNodeHasPathParams(self: INode): self is INode & NodePathParams;
export function CHNodeHasPathParams(self: CHNode | INode) {
  return (
    'path' in self &&
    'abs_path' in self &&
    typeof self.path === 'string' &&
    typeof self.abs_path === 'string' &&
    self.path !== '' &&
    self.abs_path !== ''
  );
}

export const CHNodeIsInTest = (self: CHNode) => {
  return self.flags.includes(NodeFlag.in_test);
};
export const CHNodeHasThumbnail = (self: CHNode) => {
  return !CHNodeIsFolder(self) && getDefinition(self.name).thumbnail;
};
export const CHNodeIsCoreDirectory = (self: CHNode) => {
  return CHNodeIsFolder(self) && intersection(self.flags, CORE_DIRECTORY_FLAGS).length > 0;
};

const splitNumberNonNumber = (x: string) => {
  const matches = x.match(/(\d+|\D+)/gi);
  if (matches === null) return [];
  return matches.map((x) => {
    const int = parseInt(x, 10);
    if (Number.isNaN(int)) {
      return x;
    }
    return int;
  });
};

export function CHNodeGetSortFn<T extends CHNode>(
  { key, order }: SortByInterface,
  getter?: (n: T) => string | number | Date | null,
) {
  type CompareResult = 'A_BIGGER' | 'B_BIGGER' | 'EQ';
  const sortToken = (
    a: string | number | undefined | null,
    b: string | number | undefined | null,
  ): CompareResult => {
    if (a === null || a === undefined) {
      if (b === null || b === undefined) {
        return 'EQ';
      }
      return 'B_BIGGER';
    } else if (b === null || b === undefined) {
      return 'A_BIGGER';
    }

    if (typeof a === 'string') {
      if (typeof b === 'string') {
        if (a > b) return 'A_BIGGER';
        if (b > a) return 'B_BIGGER';
        return 'EQ';
      } else {
        return 'A_BIGGER';
      }
    } else {
      if (typeof b === 'string') {
        return 'B_BIGGER';
      } else {
        if (a > b) return 'A_BIGGER';
        if (b > a) return 'B_BIGGER';
        return 'EQ';
      }
    }
  };

  return (a: T, b: T) => {
    const asc = order === 'asc';
    const coefs: Record<CompareResult, -1 | 0 | 1> = {
      EQ: 0,
      A_BIGGER: asc ? 1 : -1,
      B_BIGGER: asc ? -1 : 1,
    };

    // сначала папки

    if (CHNodeIsFolder(a) && !CHNodeIsFolder(b)) {
      return coefs['B_BIGGER'];
    }
    if (!CHNodeIsFolder(a) && CHNodeIsFolder(b)) {
      return coefs['A_BIGGER'];
    }
    type Value = string | number | null | Date;
    const getA = getter?.(a);
    const getB = getter?.(b);
    let aVal: Value = getA ?? (key in a ? (a as any)[key] : null);
    let bVal: Value = getB ?? (key in b ? (b as any)[key] : null);

    if (aVal === undefined || aVal === null || bVal === undefined || bVal === null) {
      if (aVal === undefined || aVal === null) {
        if (bVal === undefined || bVal === null) {
          return coefs['EQ'];
        }
        return coefs['B_BIGGER'];
      }
      return coefs['A_BIGGER'];
    }

    if (typeof aVal === 'string') {
      aVal = aVal.toLowerCase();
    }
    if (typeof bVal === 'string') {
      bVal = bVal.toLowerCase();
    }
    // спец случай для дат
    if (aVal instanceof Date || typeof aVal === 'number') {
      if (aVal > bVal) {
        return coefs['A_BIGGER'];
      }
      if (aVal < bVal) {
        return coefs['B_BIGGER'];
      }
      return coefs['EQ'];
    }
    if (bVal instanceof Date || typeof bVal === 'number') {
      throw new Error();
      // это выкидывание ошибки должно запутать ТС таким образом, что бы он не думал,
      // что ниже по коду может быть bVal: Date|number
      // aVal, bVal должны быть одного и того же типа и ветка с датой и числом была выше
      // Таким образом, если aVal -- не дата или число (остается только строка) а bVal -- дата или число
      // то возникла ошибка и вот мы ее и кидаем
    }

    // строки
    // проаеряем на числа
    const aTokens = splitNumberNonNumber(aVal);
    const bTokens = splitNumberNonNumber(bVal);

    const pairs = zip(aTokens, bTokens);
    for (const [aToken, bToken] of pairs) {
      const compResult = sortToken(aToken, bToken);
      if (compResult !== 'EQ') {
        return coefs[compResult];
      }
    }
    return coefs['EQ'];
  };
}
export const CHNodeDefaultSortBy: SortByInterface = { key: 'name', order: 'asc' };
